import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup } from '@angular/forms';
import { AbstractWidgetComponent, AppClientConfiguration, AppFrameContextService, AppServerAPI, CommonBrowserStorageService, ContextChange, DateUtilsService, WidgetStyle } from '@sdbn/common';
import { Subscription, interval } from 'rxjs';
import { Transport } from '../../../../../common/model/transport.model';
import { StructureNode } from '../../../timeframe-matching/structure-node';
import { GanttData, GanttSelection, Station, StationGroup } from '../../gantt-chart.model';


@Component({
  selector: 'app-gantt-chart',
  templateUrl: './gantt-chart.component.html',
  styleUrls: ['./gantt-chart.component.scss']
})
export class GanttChartWidget extends AbstractWidgetComponent implements OnInit, OnDestroy {

  COMPONENT_ID = 'tmc-yard.gantt';
  UNIT_STORAGE_KEY = 'unit';
  MINIMIZED_STORAGE_KEY = 'minimized';
  MODE_STORAGE_KEY = 'mode';
  DATE_STORAGE_KEY = 'date';
  STRUCTURE_STORAGE_KEY = 'structure';

  formGroup = new FormGroup({});

  selectedStructureNode: StructureNode;

  date = new Date();
  private ONE_DAY_DURATION: number = 24 * 60 * 60 * 1000;

  selectedTransportId: number;

  stationGroups = new Array<StationGroup>();

  stations = new Array<Station>();

  hours = new Array<number>(24);

  @ViewChild('stationGroupsElm') stationGroupsElm: ElementRef;

  @ViewChild('labelsElm') labelsElm: ElementRef;

  unit = 3;

  currentMode = 'summarized';
  currentStationGroupId: number;
  currentStationSetId: number;

  lastTransportChangedTimestamp: number;

  refreshEnabled = false;
  refreshInterval = 30000;
  refreshTimer: Subscription;

  // minuteScale = 4;
  minimized = false;

  offsetBefore = 0;
  offsetAfter = 0;

  zoomInEnabled;
  zoomOutEnabled;

  constructor(
    private appServerAPI: AppServerAPI,
    private commonBrowserStorageService: CommonBrowserStorageService,
    private appFrameContextService: AppFrameContextService,
    private dateUtilsService: DateUtilsService,
    private appClientConfiguration: AppClientConfiguration
  ) {

    super();



  }

  ngOnInit(): void {

    this.formGroup.addControl('date', new FormControl(new Date(), []));

    this.formGroup.valueChanges.subscribe(values => {
      this.date = values.date;
      this.storeDateToBrowserStorage();
      this.loadData();
    });

    this.loadModeFromBrowserStorage();
    this.restoreDate();
    this.loadStructureSelectionFromBrowserStorage();

    this.loadZoomFromBrowserStorage();
    this.loadMinimizedFromBrowserStorage();
    this.calculateOffsets();
    this.calculateHours();
    this.updateZoomEnablement();



    this.loadData();

  }

   ngOnDestroy(): void {
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }
  }


  /*

  user interaction

  */

  public setPreviewsDay(): void {
    this.date = new Date(this.date.getTime() - this.ONE_DAY_DURATION);
    this.formGroup.patchValue({
      date: this.date
    });
  }

  public setNextDay(): void {
    this.date = new Date(this.date.getTime() + this.ONE_DAY_DURATION)
    this.formGroup.patchValue({
      date: this.date
    });
  }

  public onSelectedStructureChanged(structureNode: StructureNode) {
    console.log('selection changed', structureNode);
    this.selectedStructureNode = structureNode;
    this.currentStationGroupId = this.selectedStructureNode.stationGroupId;
    this.currentStationSetId = this.selectedStructureNode.stationSetId;
    this.storeStructureSelectionToBrowserStorage();
    this.loadData();
  }

  public setMode(mode: string): void {
    this.currentMode = mode;
    this.storeModeToBrowserStorage();
    this.loadData();
  }

  zoomIn() {
    if (this.unit < 10) {
      this.unit += 1;
      this.storeZoomToBrowserStorage();
      this.updateZoomEnablement();
    }
  }

  zoomOut() {
    if (this.unit > 2) {
      this.unit -= 1;
      this.storeZoomToBrowserStorage();
      this.updateZoomEnablement();
    }
  }

  minimize() {
    this.minimized = true;
    this.calculateOffsets();
    this.storeMinimizedToBrowserStorage();
    this.calculateHours();
  }

  maximize() {
    this.minimized = false;
    this.calculateOffsets();
    this.storeMinimizedToBrowserStorage();
    this.calculateHours();
  }


  /*

  processing

  */

  linkData() {
    for (let stationGroup of this.stationGroups) {
      for (let stationRef of stationGroup.stations) {
        for (let station of this.stations) {
          if (station.id === stationRef.stationId) {
            stationGroup.stationsList.push(station);
          }
        }
      }
    }
  }





  loadData() {
    this.appServerAPI.execute(`/client/control/reporting/gantt`, new GanttSelection({
      date: this.date,
      stationGroupId: this.currentStationGroupId,
      stationSetId: this.currentStationSetId,
      mode: this.currentMode
    }))
      .subscribe(
        response => {
          let ganttData = new GanttData(response);
          this.stationGroups = ganttData.stationGroups;
          this.stations = ganttData.stations;
          this.linkData();
          this.calculateOffsets();
          this.calculateHours();
        }
      );
  }

  public selectTransport(transportId: number): void {
    this.selectedTransportId = transportId;
    this.appServerAPI.execute(`/client/control/transportoverview/transport-neu`, this.selectedTransportId)
      .subscribe(
        response => {
          this.dashboardContext.setContextValue('selected-transport', new Transport(response));
        }
      );

  }

  private registerRefreshTimer() {

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }

    if (this.appClientConfiguration.autoreload && this.refreshEnabled) {
      this.refreshTimer = interval(this.refreshInterval).subscribe(() => {
        this.loadData();
      });
    } else {
      console.log('Autoreload disabled');
    }
  }

  calculateOffsets() {

    if (this.minimized && this.stationGroups != null && this.stationGroups.length > 0) {

      let startIndex = 9999999999999;
      let endIndex = 0;

      for (const stationGroup of this.stationGroups) {
        for (const transport of stationGroup.transports) {
          let leftBound = transport.column;
          if (leftBound < startIndex) {
            startIndex = leftBound;
          }
          let rightBounds = leftBound + transport.width;
          if (rightBounds > endIndex) {
            endIndex = rightBounds;
          }
        }
      }

      let hoursBefore = Math.floor(startIndex / 60);
      let hoursAfter = Math.floor((60 * 24 - endIndex) / 60);

      this.offsetBefore = hoursBefore > 1 ? hoursBefore - 1 : 0;
      this.offsetAfter = hoursAfter > 1 ? hoursAfter - 1 : 0;

    } else {
      this.offsetBefore = 0;
      this.offsetAfter = 0;
    }


  }

  private calculateHours(): void {
    let hours = new Array<number>(24 - this.offsetBefore - this.offsetAfter);
    let index = 0;
    for (let i = this.offsetBefore; i < 24 - this.offsetAfter; i += 1) {
      hours[index++] = i;
    }
    console.log('offsets', this.offsetBefore, this.offsetAfter, hours);
    this.hours = hours;
  }

  updateZoomEnablement() {
    this.zoomOutEnabled = this.unit > 2;
    this.zoomInEnabled = this.unit < 10;
  }



  /*

  widget defaults

  */

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
    this.refreshEnabled = this.widgetReference.refreshEnabled == null ? false : this.widgetReference.refreshEnabled;
    if (this.widgetReference.refreshInterval != null && this.widgetReference.refreshInterval > 0) {
      this.refreshInterval = this.widgetReference.refreshInterval * 1000;
    } else {
      this.refreshInterval = 30 * 1000;
    }
    this.registerRefreshTimer();
  }

  /*

  browser cache handling

  */

  storeDateToBrowserStorage() {
    let isToday = this.dateUtilsService.convertDateToDatePartTimestamp(new Date()) === this.dateUtilsService.convertDateToDatePartTimestamp(this.date);
    if (isToday) {
      this.commonBrowserStorageService.remove(this.appFrameContextService.tenant.value.key, this.COMPONENT_ID, this.DATE_STORAGE_KEY);
    } else {
      this.commonBrowserStorageService.setPersistent(this.appFrameContextService.tenant.value.key, this.COMPONENT_ID, this.DATE_STORAGE_KEY, this.dateUtilsService.convertDateToDatePartTimestamp(this.date));
    }
  }

  restoreDate() {
    let value = this.commonBrowserStorageService.get(this.appFrameContextService.tenant.value.key, this.COMPONENT_ID, this.DATE_STORAGE_KEY);
    if (value != null) {
      this.date = this.dateUtilsService.parseDateFromDatePartTimestamp(value);
    } else {
      this.date = new Date();
    }
    this.formGroup.patchValue({ date: this.date });
  }

  storeZoomToBrowserStorage() {
    this.commonBrowserStorageService.setPersistent(this.appFrameContextService.tenant.value.key, this.COMPONENT_ID, this.UNIT_STORAGE_KEY, '' + this.unit);
  }

  loadZoomFromBrowserStorage() {
    let value = this.commonBrowserStorageService.get(this.appFrameContextService.tenant.value.key, this.COMPONENT_ID, this.UNIT_STORAGE_KEY);
    if (value != null) {
      this.unit = +value;
    }
  }

  loadModeFromBrowserStorage() {
    let value = this.commonBrowserStorageService.get(this.appFrameContextService.tenant.value.key, this.COMPONENT_ID, this.MODE_STORAGE_KEY);
    if (value != null) {
      this.currentMode = value;
    }
  }

  storeModeToBrowserStorage() {
    this.commonBrowserStorageService.setPersistent(this.appFrameContextService.tenant.value.key, this.COMPONENT_ID, this.MODE_STORAGE_KEY, this.currentMode);
  }

  storeMinimizedToBrowserStorage() {
    this.commonBrowserStorageService.setPersistent(this.appFrameContextService.tenant.value.key, this.COMPONENT_ID, this.MINIMIZED_STORAGE_KEY, '' + this.minimized);
  }

  loadMinimizedFromBrowserStorage() {
    let value = this.commonBrowserStorageService.get(this.appFrameContextService.tenant.value.key, this.COMPONENT_ID, this.MINIMIZED_STORAGE_KEY);
    if (value != null) {
      this.minimized = value === 'true';
    }

  }

  storeStructureSelectionToBrowserStorage() {
    this.commonBrowserStorageService.setPersistent(this.appFrameContextService.tenant.value.key, this.COMPONENT_ID, this.STRUCTURE_STORAGE_KEY, JSON.stringify(this.selectedStructureNode));
  }

  loadStructureSelectionFromBrowserStorage() {
    let value = this.commonBrowserStorageService.get(this.appFrameContextService.tenant.value.key, this.COMPONENT_ID, this.STRUCTURE_STORAGE_KEY);
    if (value != null) {
      this.selectedStructureNode = new StructureNode(JSON.parse(value));
      this.currentStationGroupId = this.selectedStructureNode.stationGroupId;
      this.currentStationSetId = this.selectedStructureNode.stationSetId;
    }

  }
}
